import React, { useEffect, useState } from "react"
import {
  Row, Col, Card, CardHeader, CardBody, Spinner, Alert,
} from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import { useNavigate, useParams } from "react-router-dom"
import {
  adminGetCoupon,
  adminGetCouponUsages,
} from "../../api/requests/subscription-requests"
import "../../assets/css/page-common.css"
import BackButton from "../../components/Common/BackButton";

const NAVY = "#221C5D"
const ORANGE = "#E56A22"
const GREEN = "#71C30B"
const RED_LT = "#E07A7A"

const formatDate = (d) => {
  if (!d) return "-"
  try { return new Date(d).toLocaleDateString("tr-TR") } catch { return d }
}

const formatDateTime = (d) => {
  if (!d) return "-"
  try {
    const date = new Date(d)
    return date.toLocaleDateString("tr-TR") + " " + date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
  } catch { return d }
}

const formatMoney = (x) => {
  if (x === undefined || x === null) return "-"
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(x) + " \u20BA"
}

const CouponDetailPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  const [coupon, setCoupon] = useState(null)
  const [usages, setUsages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const styles = `
    .coupon-detail .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .coupon-detail .btn-navy:hover { filter: brightness(1.03); }
    .coupon-detail .rounded-4 { border-radius: 1rem !important; }
    .coupon-detail table {
      width: 100%; border-collapse: separate; border-spacing: 0;
      border: 1px solid #dee2e6; border-radius: 1rem; overflow: hidden; background: #fff;
    }
    .coupon-detail table th {
      background: #f9fafb; color: #495057; font-weight: 600;
      border-bottom: 1px solid #dee2e6; border-right: 1px solid #dee2e6;
    }
    .coupon-detail table td {
      border-bottom: 1px solid #e9ecef; border-right: 1px solid #e9ecef;
      color: #212529; background-color: #fff;
    }
    .coupon-detail table tr:last-child td { border-bottom: none; }
    .coupon-detail table tr td:last-child,
    .coupon-detail table tr th:last-child { border-right: none; }
    .coupon-detail table td,
    .coupon-detail table th { padding: .85rem 1rem; vertical-align: middle; }
    .coupon-detail table tr:hover td { background: #f8f9fa; transition: background 0.2s ease-in-out; }
    .coupon-detail .usage-table thead tr { background-color: #f9f9f9; }
    .coupon-detail .usage-table th { border: 0; color: #6c757d; font-weight: 600; }
    .coupon-detail .usage-table td { vertical-align: middle; }
    .coupon-detail .usage-table-hover tbody tr:hover { background: #fcfcfd; }
    .coupon-detail .stat-card {
      border-radius: 1rem; border: 1px solid #e9ecef; padding: 1.25rem;
      text-align: center; background: #fff;
    }
    .coupon-detail .stat-value { font-size: 1.75rem; font-weight: 700; }
    .coupon-detail .stat-label { font-size: .85rem; color: #6c757d; margin-top: .25rem; }
    .coupon-detail .progress-bar-custom {
      height: 8px; border-radius: 4px; background: #e9ecef; overflow: hidden;
    }
    .coupon-detail .progress-bar-fill {
      height: 100%; border-radius: 4px; transition: width 0.3s;
    }
    @media (max-width: 768px) {
      .coupon-detail table td, .coupon-detail table th { padding: .6rem .7rem; }
    }
  `

  document.title = "Kupon Detayı | Tahsilat360"

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const [couponRes, usagesRes] = await Promise.all([
        adminGetCoupon(id),
        adminGetCouponUsages(id),
      ])
      setCoupon(couponRes.data)
      const usageData = usagesRes.data
      setUsages(Array.isArray(usageData) ? usageData : [])
    } catch (err) {
      if (err.message?.includes("401") || err.message?.includes("403")) {
        navigate("/login")
        return
      }
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [id])

  const usagePercent = coupon?.maxUsageCount
    ? Math.min((coupon.currentUsageCount / coupon.maxUsageCount) * 100, 100)
    : null

  const totalDiscount = usages.reduce((sum, u) => sum + (u.discountApplied || 0), 0)

  if (loading) {
    return (
      <div className="page-content page-common text-center p-5">
        <Spinner size="lg" color="primary" />
        <h6 className="mt-3 text-muted">Yükleniyor...</h6>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page-content page-common">
        <div className="container-fluid">
          <Alert color="danger" className="rounded-4">
            <i className="bx bx-error-circle me-2" />
            {error}
            <button className="btn btn-sm btn-outline-danger ms-2 btn-rounded" onClick={loadData}>
              Tekrar Dene
            </button>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content page-common coupon-detail">
      <style>{styles}</style>
      <div className="container-fluid">

        {/* ── Page Header ── */}
        <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <BackButton />
            <div>
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                  <code style={{ fontSize: "1.1rem", color: "#000" }}>{coupon?.code}</code>
                </h5>
                <span
                  className="badge d-inline-flex align-items-center gap-1"
                  style={{
                    borderRadius: 999,
                    padding: ".3rem .65rem",
                    backgroundColor: coupon?.active ? `${GREEN}18` : `${RED_LT}18`,
                    color: coupon?.active ? GREEN : RED_LT,
                    fontWeight: 600,
                    fontSize: ".75rem",
                    border: `1px solid ${coupon?.active ? GREEN : RED_LT}30`,
                  }}
                >
                  <i className={`bx ${coupon?.active ? "bx-check-circle" : "bx-x-circle"}`} style={{ fontSize: 13 }} />
                  {coupon?.active ? "Aktif" : "Pasif"}
                </span>
              </div>
              <span className="text-muted" style={{ fontSize: ".82rem" }}>
                Kupon Detay Sayfası
              </span>
            </div>
          </div>
        </div>

        {coupon && (
          <>
            {/* Stats & Detail */}
            <Card className="detail-card mb-3">
              <CardHeader>
                <div className="section-title">
                  <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                    <i className="bx bx-purchase-tag" />
                  </div>
                  Kupon Bilgileri
                </div>
              </CardHeader>
              <CardBody>
                <Row>
                  {[
                    { label: "Kupon Kodu", value: coupon.code },
                    { label: "İndirim Tipi", value: coupon.discountType === "PERCENTAGE" ? "Yüzde (%)" : "Sabit Tutar (TL)" },
                    { label: "İndirim Değeri", value: coupon.discountType === "PERCENTAGE" ? `%${coupon.discountValue}` : formatMoney(coupon.discountValue) },
                    { label: "Oluşturan", value: coupon.createdByUsername || "-" },
                  ].map((f, i) => (
                    <Col key={i} xs={6} md={3} className="info-field">
                      <div className="info-label">{f.label}</div>
                      <div className="info-value">{f.value || "-"}</div>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>

            {/* Stat Chips */}
            <div className="stat-chip-row mb-3">
              {[
                {
                  label: "İndirim Değeri",
                  value: coupon.discountType === "PERCENTAGE" ? `%${coupon.discountValue}` : formatMoney(coupon.discountValue),
                  color: NAVY, bg: "rgba(34,28,93,.1)", icon: "bx-purchase-tag",
                },
                {
                  label: "Kullanım",
                  value: `${coupon.currentUsageCount || 0} / ${coupon.maxUsageCount || "\u221E"}`,
                  color: ORANGE, bg: "rgba(229,106,34,.1)", icon: "bx-bar-chart-alt-2",
                },
                {
                  label: "Toplam İndirim",
                  value: formatMoney(totalDiscount),
                  color: GREEN, bg: "rgba(113,195,11,.1)", icon: "bx-wallet",
                },
                {
                  label: "Firma Başına Limit",
                  value: coupon.perCompanyLimit,
                  color: "#6c757d", bg: "rgba(108,117,125,.1)", icon: "bx-buildings",
                },
              ].map((s, i) => (
                <div key={i} className="stat-chip">
                  <div className="stat-icon" style={{ background: s.bg, color: s.color }}>
                    <i className={`bx ${s.icon}`} />
                  </div>
                  <div className="stat-number" style={{ color: s.color }}>{s.value}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Detay Kartları */}
            <Row className="mb-3 g-3">
              <Col lg={6}>
                <Card className="detail-card h-100">
                  <CardHeader>
                    <div className="section-title">
                      <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                        <i className="bx bx-info-circle" />
                      </div>
                      Kupon Detayları
                    </div>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      {[
                        {
                          label: "İndirimle Birleşebilir",
                          render: () => (
                            <span className="badge border-0" style={{
                              borderRadius: 999, padding: ".35rem .6rem", fontWeight: 600, fontSize: ".78rem",
                              backgroundColor: coupon.stackableWithDiscount ? "rgba(113,195,11,.1)" : "rgba(108,117,125,.1)",
                              color: coupon.stackableWithDiscount ? GREEN : "#6c757d",
                            }}>
                              {coupon.stackableWithDiscount ? "Evet" : "Hayır"}
                            </span>
                          ),
                        },
                        { label: "Oluşturulma Tarihi", value: formatDateTime(coupon.createdAt) },
                      ].map((f, i) => (
                        <Col key={i} sm={6} className="info-field">
                          <div className="info-label">{f.label}</div>
                          <div className="info-value">{f.render ? f.render() : (f.value || "-")}</div>
                        </Col>
                      ))}
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              <Col lg={6}>
                <Card className="detail-card h-100">
                  <CardHeader>
                    <div className="section-title">
                      <div className="section-icon" style={{ background: "rgba(229,106,34,.1)", color: ORANGE }}>
                        <i className="bx bx-calendar" />
                      </div>
                      Kullanım & Geçerlilik
                    </div>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      {[
                        { label: "Mevcut Kullanım", value: coupon.currentUsageCount || 0 },
                        { label: "Maks. Kullanım", value: coupon.maxUsageCount || "Sınırsız" },
                        { label: "Geçerlilik Başlangıcı", value: formatDate(coupon.validFrom) },
                        { label: "Geçerlilik Bitişi", value: formatDate(coupon.validUntil) },
                      ].map((f, i) => (
                        <Col key={i} sm={6} className="info-field">
                          <div className="info-label">{f.label}</div>
                          <div className="info-value">{f.value}</div>
                        </Col>
                      ))}
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Hedef Bilgileri */}
            {(coupon.applicablePlanIds?.length > 0 || coupon.targetCompanyIds?.length > 0) && (
              <Card className="detail-card mb-3">
                <CardHeader>
                  <div className="section-title">
                    <div className="section-icon" style={{ background: "rgba(113,195,11,.1)", color: GREEN }}>
                      <i className="bx bx-target-lock" />
                    </div>
                    Hedefleme
                  </div>
                </CardHeader>
                <CardBody>
                  <Row>
                    {coupon.applicablePlanIds?.length > 0 && (
                      <Col md={6} className="info-field">
                        <div className="info-label">Geçerli Paketler</div>
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {coupon.applicablePlanIds.map((pid) => (
                            <span key={pid} className="badge bg-light text-dark border" style={{ fontSize: "12px", borderRadius: 999 }}>{pid}</span>
                          ))}
                        </div>
                      </Col>
                    )}
                    {coupon.targetCompanyIds?.length > 0 && (
                      <Col md={6} className="info-field">
                        <div className="info-label">Hedef Firmalar</div>
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {coupon.targetCompanyIds.map((cid) => (
                            <span key={cid} className="badge bg-light text-dark border" style={{ fontSize: "12px", borderRadius: 999 }}>{cid}</span>
                          ))}
                        </div>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            )}

            {/* Kullanım Geçmişi */}
            <Card className="detail-card">
              <CardHeader>
                <div className="section-title">
                  <div className="section-icon" style={{ background: "rgba(229,106,34,.1)", color: ORANGE }}>
                    <i className="bx bx-history" />
                  </div>
                  Kullanım Geçmişi {usages.length > 0 && <span className="text-muted fw-normal" style={{ fontSize: ".85rem" }}>({usages.length})</span>}
                </div>
              </CardHeader>
              <CardBody className="pt-2">
                {usages.length > 0 ? (
                  <div className="table-responsive rounded-4 border">
                    <Table className="table table-hover mb-0 usage-table">
                      <Thead>
                        <Tr>
                          <Th>#</Th>
                          <Th>Firma</Th>
                          <Th>Paket</Th>
                          <Th>İndirim Tutarı</Th>
                          <Th>Kullanım Tarihi</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {usages.map((u, idx) => (
                          <Tr key={u.id} className="border-bottom">
                            <Td>{idx + 1}</Td>
                            <Td>
                              <span className="fw-semibold">{u.companyName || "-"}</span>
                              {u.companyId && (
                                <div className="text-muted small">{u.companyId.slice(0, 8)}...</div>
                              )}
                            </Td>
                            <Td>{u.planName || "-"}</Td>
                            <Td>
                              <span className="fw-semibold" style={{ color: GREEN }}>
                                {formatMoney(u.discountApplied)}
                              </span>
                            </Td>
                            <Td>{formatDateTime(u.usedAt)}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="bx bx-receipt display-4 d-block mb-2" />
                    Bu kupon henüz kullanılmamış
                  </div>
                )}
              </CardBody>
            </Card>
          </>
        )}
      </div>
    </div>
  )
}

export default CouponDetailPage
